import { Link, useLocation } from 'react-router-dom';
import { Heart, Search, MessageCircle, User, Settings, Crown, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

function UpgradeCTA({ plan }) {
  const { t } = useTranslation();

  if (plan === 'gold') {
    // Gold users: subtle badge
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20" data-testid="nav-gold-badge">
        <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
        <span className="text-xs font-semibold text-[#D4AF37]">Gold</span>
      </div>
    );
  }

  if (plan === 'premium') {
    // Premium users: encourage Gold upgrade
    return (
      <Link
        to="/pricing"
        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C09A2F] text-white text-xs font-semibold hover:shadow-md hover:shadow-[#D4AF37]/25 hover:-translate-y-[1px] transition-all"
        data-testid="nav-upgrade-gold"
      >
        <Crown className="w-3.5 h-3.5" />
        <span>{t('nav.go_gold', 'Go Gold')}</span>
      </Link>
    );
  }

  // Free users: general upgrade
  return (
    <Link
      to="/pricing"
      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#6E1A24] to-[#8A2331] text-white text-xs font-semibold hover:shadow-md hover:shadow-[#6E1A24]/25 hover:-translate-y-[1px] transition-all"
      data-testid="nav-upgrade-cta"
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span>{t('nav.upgrade', 'Upgrade')}</span>
    </Link>
  );
}

export default function Navigation() {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const isActive = (path) => location.pathname === path;
  const currentPlan = user?.subscription?.plan || user?.current_plan || 'free';

  const navItems = [
    { path: '/discover', icon: Search, label: t('nav.discover') },
    { path: '/matches', icon: MessageCircle, label: t('nav.matches') },
    { path: '/profile', icon: User, label: t('nav.profile') },
    { path: '/settings', icon: Settings, label: t('nav.settings') }
  ];

  return (
    <>
      {/* Desktop Header */}
      <header className="nav-header hidden sm:flex">
        <div className="container-premium w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Heart className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
            </Link>
            <UpgradeCTA plan={currentPlan} />
          </div>
          
          <nav className="flex items-center gap-2">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  isActive(item.path)
                    ? 'bg-[#6E1A24]/10 text-[#6E1A24]'
                    : 'text-[#6C6C67] hover:text-[#2D2D2A] hover:bg-[#F5F2EB]'
                }`}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
            
            {['admin', 'super_admin'].includes(user?.role) && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  isActive('/admin')
                    ? 'bg-[#6E1A24] text-white'
                    : 'bg-[#6E1A24]/10 text-[#6E1A24] hover:bg-[#6E1A24]/20'
                }`}
                data-testid="nav-admin"
              >
                <span className="font-medium">{t('nav.admin')}</span>
              </Link>
            )}
            
            <LanguageSwitcher />
          </nav>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="fixed top-0 w-full z-50 bg-[#FAF9F6] border-b border-[#E5E3DB] h-14 flex items-center px-4 sm:hidden">
        <Link to="/" className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-[#6E1A24]" strokeWidth={1.5} />
          <span className="text-lg font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </Link>
        
        <div className="ml-auto flex items-center gap-2">
          <UpgradeCTA plan={currentPlan} />
          <LanguageSwitcher variant="compact" />
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="nav-mobile sm:hidden">
        {navItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-1 ${
              isActive(item.path) ? 'text-[#6E1A24]' : 'text-[#6C6C67]'
            }`}
            data-testid={`nav-mobile-${item.path.replace('/', '')}`}
          >
            <item.icon className="w-6 h-6" strokeWidth={isActive(item.path) ? 2 : 1.5} />
            <span className="text-xs">{item.label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
}
